import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { UploadCloud, Loader2, CheckCircle2, AlertCircle, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const enconOrange = '#ee7203';
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Drag-and-drop (+ browse) upload modal for the "Additional Product Data" section
 * in Settings > Stock Database. Accepts EITHER of two Excel formats and the backend
 * auto-detects which one it is:
 *   - M4 Product Codes (weights + search keywords): prod_code, prod_desc, prod_net_wt, keyword1-6
 *   - Cost Factor Master (M2/coverage pricing): Code, Product Description, Calculated Factor
 * Either file merges into the existing data (upsert by product code) — it never wipes
 * data supplied by the other file type.
 */
const AdditionalProductDataUploadModal = ({ isOpen, onClose, onUploaded }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => { setResult(null); setUploading(false); setDragOver(false); };
  const handleClose = () => { reset(); onClose(); };

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`${API}/api/stock-database/additional-product-data/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult({ ok: true, ...res.data, filename: file.name });
      toast.success(res.data?.message || 'File uploaded successfully');
      onUploaded && onUploaded(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Upload failed — file type not recognised';
      setResult({ ok: false, message: detail, filename: file.name });
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-white max-w-lg" data-testid="additional-product-data-upload-modal">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2" style={{ color: enconOrange }}>
            <UploadCloud className="w-6 h-6" />
            Upload Product Data
          </DialogTitle>
          <DialogDescription>
            Drop an M4 Product Codes file (weights &amp; keywords) or a Cost Factor Master file
            (M2/coverage pricing) — we'll detect which one it is and merge it in.
          </DialogDescription>
        </DialogHeader>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          data-testid="additional-product-data-dropzone"
          className="mt-2 flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer transition-colors"
          style={{
            borderColor: dragOver ? enconOrange : '#d1d5db',
            background: dragOver ? `${enconOrange}0d` : '#fafafa',
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => { doUpload(e.target.files?.[0]); e.target.value = ''; }}
            data-testid="additional-product-data-file-input"
          />
          {uploading ? (
            <>
              <Loader2 className="w-9 h-9 mb-2 animate-spin" style={{ color: enconOrange }} />
              <p className="text-sm font-medium text-gray-500">Detecting &amp; merging…</p>
            </>
          ) : (
            <>
              <UploadCloud className="w-9 h-9 mb-2" style={{ color: enconOrange }} />
              <p className="text-sm text-gray-600"><span className="font-semibold">Click to browse</span> or drag &amp; drop</p>
              <p className="text-xs text-gray-400 mt-1">M4 Product Codes (.xlsx) or Cost Factor Master (.xlsx)</p>
            </>
          )}
        </div>

        {result && (
          <div
            data-testid="additional-product-data-upload-result"
            className={`flex items-start gap-3 p-3 rounded-xl border text-sm ${result.ok ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
          >
            {result.ok ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            )}
            <div>
              {result.ok && result.file_type && (
                <p className="font-semibold text-gray-800 flex items-center gap-1.5">
                  <FileSpreadsheet className="w-3.5 h-3.5" style={{ color: enconOrange }} />
                  Detected: {result.file_type === 'cost_factor_master' ? 'Cost Factor Master' : 'M4 Product Codes'}
                </p>
              )}
              <p className={result.ok ? 'text-green-700' : 'text-red-600'}>{result.message}</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdditionalProductDataUploadModal;
